import _ from 'underscore';
import { AxiosResponse } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import validateProfileOwner from 'utils/validate-profile-owner';

import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import DatePicker from 'components/date-picker';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import dayjs from 'dayjs';
import { getProfilesAssortment } from 'views/planning-prognosis/api/get-profiles';
import { getDefaultSiteProfile } from '../api/get-site-profile';
import { putDefaultSiteProfile } from '../api/update-site-profile';
import { UpdateDefaultSiteProfileInput } from '../types/update-default-site-profile-input.type';

type Props = {
  globalSiteId: string;
};

const PAGE_NAME_ID = 'profilesStrategies.editSiteProfiles';

const EditSiteProfiles: React.FC<Props> = ({ globalSiteId }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #region STATE
  const [searchValue, setSearchValue] = useState<{ [rowIdx: number]: { [fieldName: string]: string } }>({});
  // #endregion STATE

  // FORM
  const { handleSubmit, control, reset } = useForm<UpdateDefaultSiteProfileInput>({
    defaultValues: {
      harvestTarget: {
        harvestByWeight: true,
        harvestTargetDate: dayjs().utc(true).toISOString(),
        harvestTargetWeight: 1200,
      },
      smoltTyped: [],
    },
    shouldUnregister: false,
  });

  const { fields: fieldsProfiles } = useFieldArray({
    control: control,
    name: 'smoltTyped',
    shouldUnregister: false,
  });

  const watch = useWatch({
    control: control,
    name: 'smoltTyped',
  });

  const harvestByWeight = useWatch({
    control: control,
    name: 'harvestTarget.harvestByWeight',
  });

  // FETCH DATA
  const { data: defaultProfiles, isFetching } = useGet(
    ['getPlanAndPrognosisSiteDefaultProfile', globalSiteId],
    ({ queryKey }) => getDefaultSiteProfile(String(queryKey[1])),
    {
      enabled: account.company?.features.hasPlanningPrognosisManageCompanyOrSiteProfiles && !!globalSiteId,
    }
  );

  useEffect(() => {
    if (!defaultProfiles) return;

    reset({
      harvestTarget: {
        harvestByWeight: defaultProfiles.harvestTarget.harvestByWeight,
        harvestTargetDate: defaultProfiles.harvestTarget.harvestTargetDate,
        harvestTargetWeight: defaultProfiles.harvestTarget.harvestTargetWeight,
      },
      smoltTyped: defaultProfiles.smoltTyped.map((smoltType) => ({
        efiProfileId: smoltType.efi?.profileId ?? 0,
        egiProfileId: smoltType.egi?.profileId ?? 0,
        feedStrategyId: smoltType.feedStrategy?.profileId ?? 0,
        mortalityProfileId: smoltType.mortality?.profileId ?? 0,
        smoltTypeId: smoltType.smoltTypeId ?? 0,
      })),
    });
  }, [defaultProfiles, reset]);

  const { data: profiles } = useGet(
    ['getProfilesAssortment', account.company?.marketId, account.company?.companyId],
    ({ queryKey }) => getProfilesAssortment(Number(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.marketId && !!account.company?.companyId,
    }
  );

  // POST DATA
  const updateDefaultProfile = usePost<AxiosResponse, UpdateDefaultSiteProfileInput>(
    (request) => putDefaultSiteProfile(globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getPlanAndPrognosisMarketDefaultProfile', account.company?.marketId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleUpdateProfiles = (request: UpdateDefaultSiteProfileInput) => {
    if (!!defaultProfiles) {
      updateDefaultProfile.mutate({
        ...request,
        temperatureProfileId: defaultProfiles.temperatureProfile?.profileId ?? null,
        fallbackEfiProfileId: defaultProfiles.efiFallback?.profileId ?? null,
        fallbackEgiProfileId: defaultProfiles.egiFallback?.profileId ?? null,
        fallbackMortalityProfileId: defaultProfiles.mortalityFallback?.profileId ?? null,
      });
    }
  };

  // #region RENDERS
  const SelectContainer = isMobile ? Box : React.Fragment;
  const selectOptions = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.egiLabel` }),
        name: 'egiProfileId',
        options: profiles?.egi,
      },
      {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.efiLabel` }),
        name: 'efiProfileId',
        options: profiles?.efi,
      },
      {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.mortalityLabel` }),
        name: 'mortalityProfileId',
        options: profiles?.mort,
      },
      {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedStrategyLabel` }),
        name: 'feedStrategyId',
        options: profiles?.feed,
      },
    ],
    [profiles]
  );
  // #endregion RENDERS

  return (
    <form onSubmit={handleSubmit(handleUpdateProfiles)}>
      <Typography variant="h6" color={theme.palette.common.white} sx={{ paddingBottom: 2 }}>
        <FormattedMessage id={`${PAGE_NAME_ID}.harvestTargetLabel`} />
      </Typography>
      <Box display={'flex'} gap={4}>
        <Switch
          label={
            harvestByWeight ? intl.formatMessage({ id: `${PAGE_NAME_ID}.weight` }) : intl.formatMessage({ id: `${PAGE_NAME_ID}.date` })
          }
          controller={{
            control: control,
            name: 'harvestTarget.harvestByWeight',
          }}
        />
        <Box>
          {harvestByWeight ? (
            <TextField
              type="number"
              textAlign="right"
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.weight` })}
              disabled={isFetching || updateDefaultProfile.isPending}
              controller={{
                control: control,
                name: 'harvestTarget.harvestTargetWeight',
              }}
              endAdornment={'g'}
            />
          ) : (
            <DatePicker
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.date` })}
              disabled={isFetching || updateDefaultProfile.isPending}
              controller={{
                control: control,
                name: 'harvestTarget.harvestTargetDate',
              }}
            />
          )}
        </Box>
      </Box>
      <Typography variant="h3" color={theme.palette.common.white} paddingTop={4} paddingBottom={4}>
        <FormattedMessage id={`${PAGE_NAME_ID}.defaultProfiles`} />
      </Typography>
      <List
        options={[
          { id: 'speciesName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.speciesNameLabel` }) },
          { id: 'smoltTypeName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.smoltTypeNameLabel` }) },
          { id: 'egi', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.egiLabel` }) },
          { id: 'efi', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.efiLabel` }) },
          { id: 'mortality', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.mortalityLabel` }) },
          { id: 'feedStrategy', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedStrategyLabel` }) },
        ]}
        isLoading={isFetching}
        noResult={{
          show: !!defaultProfiles && defaultProfiles.smoltTyped?.length <= 0,
          text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
        }}>
        {fieldsProfiles.map((row, idx) => (
          <ListRow key={row.id}>
            <ListCell align="left">{defaultProfiles?.smoltTyped.find((s) => s.smoltTypeId === row.smoltTypeId)?.speciesName}</ListCell>
            <ListCell align="left">
              {defaultProfiles?.smoltTyped.find((s) => s.smoltTypeId === row.smoltTypeId)?.smoltTypeName}
            </ListCell>
            <SelectContainer {...(isMobile && { display: 'flex', flexDirection: 'column', gap: 2 })}>
              {selectOptions.map((select, selectId) => {
                const SelectWrapper = isMobile ? Box : ListCell;
                const currentSelected = watch[idx]?.[select.name as keyof (typeof watch)[typeof idx]];
                return (
                  <SelectWrapper key={selectId}>
                    <Select
                      dark
                      hiddenLabel
                      label={select.label}
                      fullWidth
                      search={{
                        value: searchValue[idx]?.[select.name] || '',
                        onChange: (v) =>
                          setSearchValue((prev) => ({
                            ...prev,
                            [idx]: { ...prev[idx], [select.name]: v },
                          })),
                      }}
                      disabled={updateDefaultProfile.isPending}
                      controller={{
                        control: control,
                        name: `smoltTyped.${idx}.${select.name}`,
                        rules: {
                          required: true,
                          validate: {
                            invalidId: (v) => v !== '' && v !== 0,
                          },
                        },
                      }}>
                      <RadioSelectItem value={0} disabled sx={{ display: 'none' }} />
                      {select.options &&
                        _.chain(select.options)
                          ?.filter((profile) => {
                            const searchTerm = (searchValue[idx]?.[select.name] || '').toLowerCase();
                            const matchesSearch = (profile.name || '').toLowerCase().includes(searchTerm);
                            const isSelected = profile.id === currentSelected;
                            return (
                              isSelected ||
                              (profile.active &&
                                matchesSearch &&
                                validateProfileOwner(
                                  profile,
                                  account.company?.marketId,
                                  String(account.company?.companyId),
                                  globalSiteId
                                ))
                            );
                          })
                          .sortBy((p) => p.name.toLowerCase())
                          .value()
                          .map((row) => (
                            <RadioSelectItem
                              key={row.id}
                              value={row.id}
                              selected={row.id === watch[idx]?.[select.name as keyof (typeof watch)[typeof idx]]}>
                              {row.name} {select.name === 'egiProfileId' && row.ownerType}
                            </RadioSelectItem>
                          ))}
                    </Select>
                  </SelectWrapper>
                );
              })}
            </SelectContainer>
          </ListRow>
        ))}
      </List>
      <Box display="flex" justifyContent="flex-end" my={4}>
        <Button type="submit" size="medium" disabled={updateDefaultProfile.isPending || isFetching}>
          {updateDefaultProfile.isPending ? (
            <CircularProgress color="inherit" />
          ) : (
            <FormattedMessage id="general.submit" values={{ type: 'profiles' }} />
          )}
        </Button>
      </Box>
    </form>
  );
};

export default React.memo(EditSiteProfiles);
